import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "./Logo";

const STATS = [
  { value: "8", label: "Modul Terpadu" },
  { value: "99.99%", label: "Uptime & Backup" },
  { value: "14 Hari", label: "Uji Coba Gratis" },
];

export const Hero = ({ onTrial, onLogin }) => {
  const [preview, setPreview] = useState("");

  return (
    <section id="hero" className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
      <div className="pointer-events-none absolute inset-0 grid-bg" />
      <div className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 animate-pulse-glow rounded-full bg-[radial-gradient(circle,rgba(0,240,255,0.12)_0%,rgba(123,47,190,0.06)_45%,transparent_75%)]" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-950/40 px-4 py-1.5 text-xs font-medium text-cyan-300" data-testid="hero-badge">
              <Sparkles className="h-3.5 w-3.5" />
              Platform Manajemen Sekolah Digital #1 di Indonesia
            </div>

            <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Satu Portal untuk <span className="text-gradient">Seluruh Kebutuhan</span> Sekolah Anda
            </h1>

            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 lg:text-lg">
              Akademik, SPP, penggajian, absensi, LMS, hingga buku penghubung orang tua — semuanya dalam satu sistem cerdas dengan subdomain khusus sekolah Anda:{" "}
              <span className="font-mono text-cyan-400">namasekolah.sahabatpena.id</span>
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                size="lg"
                onClick={onTrial}
                data-testid="hero-trial-btn"
                className="group h-12 bg-gradient-to-r from-cyan-400 to-violet-600 px-7 text-base font-semibold text-[#060A12] transition-all hover:shadow-[0_0_28px_rgba(0,240,255,0.45)]"
              >
                Coba Gratis 14 Hari
                <ArrowRight className="ml-1 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={onLogin}
                data-testid="hero-login-btn"
                className="h-12 border-cyan-500/30 bg-transparent px-7 text-base font-semibold text-slate-100 hover:border-cyan-400/60 hover:bg-cyan-500/10"
              >
                <PlayCircle className="mr-1 h-5 w-5" />
                Login ke Sekolah Saya
              </Button>
            </div>

            <div className="mt-6 flex items-center gap-2 text-sm text-slate-400">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              Tanpa kartu kredit • Data terisolasi & aman per sekolah
            </div>

            <div className="mt-10 grid max-w-md grid-cols-3 gap-4">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`hero-stat-${s.label}`}>
                  <div className="font-heading text-2xl font-bold text-white lg:text-3xl">{s.value}</div>
                  <div className="mt-1 text-xs text-slate-400">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative"
          >
            <div className="animate-float rounded-2xl border border-cyan-500/20 glass p-2 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
              <div className="rounded-xl bg-[#0A0F1D] p-5">
                <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                  <Logo />
                  <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10px] font-medium text-emerald-400">
                    ● Live
                  </span>
                </div>
                <div className="mt-4 flex items-center gap-2 rounded-lg bg-slate-900/70 px-3 py-2 font-mono text-xs text-slate-400">
                  <span className="text-cyan-400">https://</span>
                  <input
                    value={preview}
                    onChange={(e) => setPreview(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    placeholder="namasekolah"
                    data-testid="hero-preview-subdomain"
                    className="w-24 bg-transparent text-white outline-none placeholder:text-slate-600"
                  />
                  <span>.sahabatpena.id</span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {["Siswa", "Guru", "SPP", "Absensi", "Rapor", "LMS"].map((m, i) => (
                    <div
                      key={m}
                      className="rounded-lg border border-slate-800 bg-slate-900/50 p-3 text-center transition-colors hover:border-cyan-500/40"
                      style={{ animationDelay: `${i * 0.1}s` }}
                    >
                      <div className="mx-auto mb-2 h-7 w-7 rounded-md bg-gradient-to-br from-cyan-400/20 to-violet-600/20" />
                      <div className="text-[11px] text-slate-300">{m}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 rounded-lg bg-gradient-to-r from-cyan-500/10 to-violet-600/10 p-3">
                  <div className="mb-2 flex justify-between text-[11px] text-slate-400">
                    <span>Pembayaran SPP Bulan Ini</span>
                    <span className="text-cyan-400">82%</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
                    <div className="h-full w-[82%] rounded-full bg-gradient-to-r from-cyan-400 to-violet-500" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
